// =============================================================================
// SAISIE D'UNE VISITE — quitter et reprendre sans perte
// =============================================================================
// 🔑 CE COMPOSANT NE GARDE AUCUN ÉTAT MÉTIER LOCALEMENT. Chaque réponse part au
// serveur, et l'écran REND CE QUE LE SERVEUR RENVOIE. ⛔ Pas d'état local
// reconstitué : c'est exactement ce qui fait croire qu'on a sauvegardé alors
// qu'on a seulement affiché.
//
// ⚠️ Les MANQUES et les LIGNES DE DEVIS viennent du serveur eux aussi. Les
// recalculer ici créerait une seconde autorité qui divergerait — la même faute
// que les sept emplacements de la phrase CEE.
// =============================================================================
import React, { useCallback, useEffect, useState } from "react";
import { Camera, Check, FileText, Loader2, MapPin, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";

// 🔴 MARQUEUR DE BUNDLE — ASCII pur, et surtout RENDU À L'ÉCRAN.
// ⚠️ Ma première version l'exportait depuis `VisitesModule` sans jamais
// l'afficher : le bundler l'a ÉLIMINÉ (tree-shaking) et le contrôle rendait 0.
// 🔑 Un marqueur non rendu ⛔ ne prouve RIEN — c'est « déclaré ≠ chargé »
// appliqué au marqueur lui-même.
// ⚠️ ET UN SECOND PIÈGE, PLUS DANGEREUX : après correction, le contrôle rendait
// ENCORE 0 — cette fois à cause d'un ARTEFACT DE BUILD PÉRIMÉ dans le même
// répertoire de sortie. 🔑 Construire dans un répertoire NEUF est la seule
// lecture fiable : un bundle qu'on n'a pas vu naître ⛔ ne prouve rien non plus.
// ⚠️ MARQUEUR STABLE, ⛔ PAS VERSIONNÉ. Je l'ai incrémenté deux fois (V2, V3)
// et chaque incrément a CASSÉ les bancs antérieurs, qui épinglaient l'ancienne
// valeur — ils concluaient « marqueur ABSENT » alors que la brique était bien
// servie. 🔑 Un marqueur désigne LA BRIQUE ; ce qu'une version apporte se
// vérifie par SES PROPRES `data-testid`, ⛔ pas en renommant le repère commun.
export const MARQUEUR_SAISIE = "OH-VISITE-SAISIE";
// ⚠️ Marqueur STABLE (cf. plus haut) : ce lot se distingue par ses
// `data-testid` — `photo-section`, `photo-erreur` — ⛔ pas par un suffixe.

const api = async (url, options = {}) => {
  const r = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.detail || `Erreur ${r.status}`);
  return j;
};

export default function SaisieVisite({ profilCode, profilLibelle, onOuvrirDevis }) {
  const [dossiers, setDossiers] = useState([]);
  const [dossierId, setDossierId] = useState("");
  const [devisId, setDevisId] = useState("");
  const [profil, setProfil] = useState(null);
  const [visite, setVisite] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [devisOccupe, setDevisOccupe] = useState(false);
  // 🔴 LES DEUX MOTIFS DU SERVEUR SONT GARDÉS SÉPARÉMENT ET RENDUS TELS QUELS.
  // ⚠️ Les fondre en un « impossible de créer le devis » annulerait tout le
  // travail du service : c'est exactement ce qui envoie corriger la MAUVAISE
  // chose. L'écran ⛔ ne résume PAS, il TRANSMET.
  const [devisRefus, setDevisRefus] = useState(null);
  const [photoOccupee, setPhotoOccupee] = useState(null);
  const [photoErreur, setPhotoErreur] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, p] = await Promise.all([
          api("/api/metier/dossiers?limit=50"),
          api(`/api/visite/profils/${profilCode}`),
        ]);
        setDossiers(Array.isArray(d) ? d : d?.dossiers || []);
        setProfil(p);
      } catch (e) {
        setErreur(e.message);
      }
    })();
  }, [profilCode]);

  const sensEntree = profil?.workflow?.devis === "entree";

  const demarrer = async () => {
    setErreur(null);
    setOccupe(true);
    try {
      // 🔑 « Ouvrir » REND la visite en cours si elle existe : reprendre est le
      // cas NORMAL, ⛔ pas une exception.
      const v = await api("/api/visite/saisies", {
        method: "POST",
        body: JSON.stringify({
          dossier_id: dossierId,
          profil_code: profilCode,
          devis_id: devisId || null,
        }),
      });
      setVisite(await api(`/api/visite/saisies/${v.id}`));
    } catch (e) {
      setErreur(e.message);
    } finally {
      setOccupe(false);
    }
  };

  const relire = useCallback(async (id) => {
    setVisite(await api(`/api/visite/saisies/${id}`));
  }, []);

  const repondre = async (sectionId, questionId, valeur, quantite) => {
    setErreur(null);
    try {
      await api(`/api/visite/saisies/${visite.id}/reponse`, {
        method: "PATCH",
        body: JSON.stringify({
          section_id: sectionId,
          question_id: questionId,
          valeur,
          quantite: quantite === "" || quantite === undefined ? null
            : Number(quantite),
        }),
      });
      await relire(visite.id);       // ⛔ on RELIT, on ne devine pas
    } catch (e) {
      setErreur(e.message);
    }
  };

  const observer = async (sectionId, texte) => {
    try {
      await api(`/api/visite/saisies/${visite.id}/observation`, {
        method: "PATCH",
        body: JSON.stringify({ section_id: sectionId, texte }),
      });
      await relire(visite.id);
    } catch (e) {
      setErreur(e.message);
    }
  };

  const creerDevis = async () => {
    setDevisRefus(null);
    setDevisOccupe(true);
    try {
      // ⛔ IDEMPOTENT CÔTÉ SERVEUR : si le devis existe déjà, la route REND le
      // même. L'écran n'a donc AUCUNE décision à prendre — il ouvre ce qu'on
      // lui rend. 🔑 Un « existe déjà ? » calculé ici serait une seconde
      // autorité, qui divergerait.
      const r = await api(`/api/visite/saisies/${visite.id}/devis`, {
        method: "POST",
      });
      await relire(visite.id);
      if (r?.devis?.id && onOuvrirDevis) {
        onOuvrirDevis(r.devis.id, visite.dossier_id);
      } else if (r?.devis?.id) {
        setDevisRefus("Devis créé, mais la navigation n'est pas disponible "
          + "depuis cet écran.");
      }
    } catch (e) {
      setDevisRefus(e.message);
    } finally {
      setDevisOccupe(false);
    }
  };

  const cloturer = async () => {
    setDevisRefus(null);
    setDevisOccupe(true);
    try {
      await api(`/api/visite/saisies/${visite.id}/cloturer`, { method: "POST" });
      await relire(visite.id);
    } catch (e) {
      // ⛔ Le motif du serveur NOMME ce qui reste — on le rend tel quel.
      setDevisRefus(e.message);
    } finally {
      setDevisOccupe(false);
    }
  };

  const envoyerPhoto = async (sectionId, fichier) => {
    if (!fichier) return;
    setPhotoErreur(null);
    setPhotoOccupee(sectionId);
    try {
      // 🔑 `multipart/form-data` : ⛔ PAS d'en-tête Content-Type posé à la main,
      // le navigateur doit écrire lui-même la frontière (boundary).
      const corps = new FormData();
      corps.append("section_id", sectionId);
      corps.append("fichier", fichier);
      const r = await fetch(`/api/visite/saisies/${visite.id}/photos`, {
        method: "POST", credentials: "include", body: corps,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `Erreur ${r.status}`);
      await relire(visite.id);        // ⛔ on RELIT, on ne devine pas
    } catch (e) {
      // ⛔ LE MOTIF DU SERVEUR EST RENDU TEL QUEL. Il dit si Zoho a refusé, si
      // le dossier n'a pas de dossier Zoho, ou si l'état est indécidable —
      // trois causes DIFFÉRENTES, trois corrections différentes.
      setPhotoErreur({ section: sectionId, message: e.message });
    } finally {
      setPhotoOccupee(null);
    }
  };

  const rep = (s, q) => (visite?.reponses?.[s] || {})[q] || {};

  // ── AVANT DÉMARRAGE : choisir le dossier ─────────────────────────────
  if (!visite) {
    return (
      <div
        style={{
          background: "var(--oh-bg-surface)",
          border: "0.5px solid var(--oh-border)",
          borderRadius: 8,
          padding: 16,
        }}
        data-testid="saisie-demarrage"
      >
        <div style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-sm mb-3">
          Démarrer « {profilLibelle} »
        </div>
        <span className="text-[10px] opacity-40">{MARQUEUR_SAISIE}</span>
        {/* 🔴 LE SENS DU DEVIS EST DIT À L'UTILISATEUR, ⛔ pas seulement appliqué. */}
        {profil?.workflow?.libelle_parcours && (
          <div
            style={{
              background: "var(--oh-stat-blue-bg)",
              color: "var(--oh-stat-blue-text)",
              borderRadius: 8,
            }}
            className="text-xs px-3 py-2 mb-3"
            data-testid="saisie-parcours"
          >
            {profil.workflow.libelle_parcours}
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-sm">
            <span style={{ color: "var(--oh-text-secondary)" }} className="block text-xs mb-1">
              Dossier
            </span>
            <select
              value={dossierId}
              onChange={(e) => setDossierId(e.target.value)}
              data-testid="saisie-dossier"
              aria-label="Dossier de la visite"
              style={{
                background: "var(--oh-bg-elevated)",
                color: "var(--oh-text)",
                border: "0.5px solid var(--oh-border)",
                borderRadius: 8,
                height: 36,
                minWidth: 260,
              }}
              className="px-2"
            >
              <option value="">Choisir un dossier…</option>
              {dossiers.map((d) => (
                <option key={d.id || d._id} value={d.id || d._id}>
                  {d.reference || d.id}
                </option>
              ))}
            </select>
          </label>
          {/* ⚠️ Devis en ENTRÉE : la prestation est VENDUE, le devis précède. */}
          {sensEntree && (
            <label className="text-sm">
              <span style={{ color: "var(--oh-text-secondary)" }} className="block text-xs mb-1">
                Devis accepté (requis)
              </span>
              <input
                value={devisId}
                onChange={(e) => setDevisId(e.target.value)}
                placeholder="Numéro du devis accepté"
                data-testid="saisie-devis"
                style={{
                  background: "var(--oh-bg-elevated)",
                  color: "var(--oh-text)",
                  border: "0.5px solid var(--oh-border)",
                  borderRadius: 8,
                  height: 36,
                  minWidth: 220,
                }}
                className="px-2"
              />
            </label>
          )}
          <Button
            onClick={demarrer}
            disabled={!dossierId || occupe}
            data-testid="saisie-ouvrir"
            style={{
              background: "var(--oh-accent-solid)",
              color: "var(--oh-accent-contrast)",
            }}
          >
            {occupe ? <Loader2 size={14} className="animate-spin" /> : "Ouvrir la visite"}
          </Button>
        </div>
        {erreur && (
          <div
            style={{
              background: "var(--oh-stat-red-bg)",
              color: "var(--oh-stat-red-text)",
              borderRadius: 8,
            }}
            className="text-sm mt-3 p-3 whitespace-pre-line"
            data-testid="saisie-erreur"
          >
            {erreur}
          </div>
        )}
      </div>
    );
  }

  // ── CE QUI EMPÊCHE DE CLÔTURER — DEUX CAUSES DISTINCTES ───────────────
  // 🔴 Les deux viennent du SERVEUR, séparément (`manques` / `reserves_bloquantes`).
  // ⛔ On ⛔ ne les additionne PAS et on ⛔ ne les recalcule PAS ici : c'est
  // `saisie.cloturer()` qui décide, l'écran ⛔ ne fait que DIRE la même chose.
  // ⚠️ Avant ce jour, le bouton ⛔ ne regardait QUE `manques` : avec 0 manque et
  // une réserve bloquante il restait ACTIF, l'utilisateur cliquait, et le refus
  // du backend tombait dans l'erreur générique — sans jamais nommer la cause.
  const bloquantes = visite.reserves_bloquantes || [];
  const nbBloquantes = bloquantes.length;
  const nbManques = (visite.manques || []).length;
  const cloturePossible = nbManques === 0 && nbBloquantes === 0;

  // ── LA SAISIE ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-3" data-testid="saisie-en-cours">
      <div
        style={{
          background: "var(--oh-stat-green-bg)",
          color: "var(--oh-stat-green-text)",
          borderRadius: 8,
        }}
        className="text-xs px-3 py-2"
      >
        Visite ouverte sur {visite.dossier_reference} — chaque réponse est
        enregistrée immédiatement. Vous pouvez fermer et reprendre.
      </div>

      {erreur && (
        <div
          style={{
            background: "var(--oh-stat-red-bg)",
            color: "var(--oh-stat-red-text)",
            borderRadius: 8,
          }}
          className="text-sm p-3 whitespace-pre-line"
          data-testid="saisie-erreur"
        >
          {erreur}
        </div>
      )}

      {(profil?.sections || []).map((s) => (
        <div
          key={s.id}
          style={{
            background: "var(--oh-bg-surface)",
            border: "0.5px solid var(--oh-border)",
            borderRadius: 8,
            padding: 12,
          }}
          data-testid={`section-${s.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {s.type === "zone" ? <MapPin size={14} /> : <Check size={14} />}
            <span style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-sm">
              {s.libelle}
            </span>
            {/* 🔴 LE BOUTON PHOTO. La brique « existe » depuis le 10/09 ;
                c'est lui qui la rend ACCESSIBLE à un visiteur.
                ⚠️ `capture="environment"` ouvre l'appareil arrière sur mobile —
                un visiteur sur chantier ⛔ ne cherche PAS dans sa galerie. */}
            <label
              data-testid="photo-section"
              style={{
                borderColor: "var(--oh-accent)",
                color: "var(--oh-accent-text)",
                borderRadius: 8,
                borderWidth: "0.5px",
                borderStyle: "solid",
              }}
              className="text-xs px-2 py-1 flex items-center gap-1 cursor-pointer"
            >
              <Camera size={12} />
              {photoOccupee === s.id ? "Envoi…" : "Ajouter une photo"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                aria-label={`Ajouter une photo pour ${s.libelle}`}
                disabled={photoOccupee === s.id}
                onChange={(e) => {
                  envoyerPhoto(s.id, e.target.files?.[0]);
                  e.target.value = "";   // ⛔ pour pouvoir reprendre la MÊME photo
                }}
              />
            </label>
            {/* Ce que la section porte DÉJÀ — venu du serveur, ⛔ pas du local. */}
            {(visite.photos?.[s.id] || []).length > 0 && (
              <span
                style={{
                  background: "var(--oh-stat-green-bg)",
                  color: "var(--oh-stat-green-text)",
                  borderRadius: 8,
                }}
                className="text-xs px-2 py-0.5"
                data-testid={`photo-compte-${s.id}`}
              >
                {visite.photos[s.id].length} photo(s)
              </span>
            )}
            {s.medias?.photos?.requis && !(visite.photos?.[s.id] || []).length && (
              <span
                style={{
                  background: "var(--oh-stat-amber-bg)",
                  color: "var(--oh-stat-amber-text)",
                  borderRadius: 8,
                }}
                className="text-xs px-2 py-0.5"
              >
                photo requise
              </span>
            )}
          </div>

          <div className="space-y-2">
            {(s.questions || []).map((q) => {
              const r = rep(s.id, q.id);
              return (
                <div key={q.id} className="flex flex-wrap items-center gap-2">
                  {q.type === "case" ? (
                    <label className="flex items-center gap-2 text-sm" style={{ color: "var(--oh-text)" }}>
                      <input
                        type="checkbox"
                        checked={!!r.valeur}
                        data-testid={`q-${s.id}-${q.id}`}
                        onChange={(e) => repondre(s.id, q.id, e.target.checked,
                          e.target.checked ? undefined : "")}
                      />
                      {q.libelle}
                    </label>
                  ) : (
                    <label className="flex items-center gap-2 text-sm" style={{ color: "var(--oh-text)" }}>
                      <span className="min-w-[220px]">{q.libelle}</span>
                      {q.type === "choix" ? (
                        <select
                          value={r.valeur ?? ""}
                          data-testid={`q-${s.id}-${q.id}`}
                          aria-label={q.libelle}
                          onChange={(e) => repondre(s.id, q.id, e.target.value)}
                          style={{
                            background: "var(--oh-bg-elevated)",
                            color: "var(--oh-text)",
                            border: "0.5px solid var(--oh-border)",
                            borderRadius: 8,
                            height: 36,
                          }}
                          className="px-2"
                        >
                          <option value="">—</option>
                          {(q.options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={q.type === "nombre" ? "number" : "text"}
                          defaultValue={r.valeur ?? ""}
                          placeholder={q.unite ? `en ${q.unite}` : q.libelle}
                          data-testid={`q-${s.id}-${q.id}`}
                          aria-label={q.libelle}
                          onBlur={(e) => repondre(s.id, q.id,
                            q.type === "nombre" ? Number(e.target.value) : e.target.value)}
                          style={{
                            background: "var(--oh-bg-elevated)",
                            color: "var(--oh-text)",
                            border: "0.5px solid var(--oh-border)",
                            borderRadius: 8,
                            height: 36,
                          }}
                          className="px-2"
                        />
                      )}
                    </label>
                  )}

                  {/* 🔴 LA QUANTITÉ — elle n'apparaît que si le travail est
                      RETENU, parce qu'elle n'a de sens que là. C'est elle qui
                      produira la ligne de devis. */}
                  {q.type === "case" && r.valeur && (
                    <span className="flex items-center gap-1">
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={r.quantite ?? ""}
                        placeholder="quantité"
                        data-testid={`qte-${s.id}-${q.id}`}
                        aria-label={`Quantité pour ${q.libelle}`}
                        onBlur={(e) => repondre(s.id, q.id, true, e.target.value)}
                        style={{
                          background: "var(--oh-bg-elevated)",
                          color: "var(--oh-text)",
                          border: "0.5px solid var(--oh-border)",
                          borderRadius: 8,
                          height: 32,
                          width: 96,
                        }}
                        className="px-2 text-sm"
                      />
                      <span style={{ color: "var(--oh-text-muted)" }} className="text-xs">
                        {q.unite_quantite || "u"}
                      </span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {photoErreur?.section === s.id && (
            <div
              style={{
                background: "var(--oh-stat-red-bg)",
                color: "var(--oh-stat-red-text)",
                borderRadius: 8,
              }}
              className="text-xs mt-2 p-2 whitespace-pre-line"
              data-testid="photo-erreur"
            >
              {photoErreur.message}
            </div>
          )}

          <textarea
            defaultValue={visite.observations?.[s.id]?.texte || ""}
            placeholder="Observations…"
            data-testid={`obs-${s.id}`}
            aria-label={`Observations pour ${s.libelle}`}
            onBlur={(e) => observer(s.id, e.target.value)}
            rows={2}
            style={{
              background: "var(--oh-bg-elevated)",
              color: "var(--oh-text)",
              border: "0.5px solid var(--oh-border)",
              borderRadius: 8,
              width: "100%",
            }}
            className="mt-2 p-2 text-sm"
          />
        </div>
      ))}

      {/* ── CE QUE LA VISITE PRODUIT — venu du SERVEUR ─────────────────── */}
      {visite.sens_devis === "sortie" && (
        <div
          style={{
            background: "var(--oh-bg-elevated)",
            border: "0.5px solid var(--oh-border)",
            borderRadius: 8,
            padding: 12,
          }}
          data-testid="saisie-lignes-devis"
        >
          <div style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-sm mb-2 flex items-center gap-2">
            <FileText size={14} /> Lignes de devis produites par cette visite
          </div>
          {(visite.lignes_devis || []).length === 0 ? (
            <div style={{ color: "var(--oh-text-secondary)" }} className="text-sm">
              Aucune pour l'instant — cochez un travail et donnez sa quantité.
            </div>
          ) : (
            <ul className="text-sm space-y-1">
              {visite.lignes_devis.map((l, i) => (
                <li key={i} style={{ color: "var(--oh-text)" }}>
                  {l.designation} — <strong>{l.quantite} {l.unite}</strong>
                </li>
              ))}
            </ul>
          )}
          {/* 🔴 LE MAILLON QUI FERME LE PARCOURS. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={creerDevis}
              disabled={devisOccupe}
              data-testid="creer-devis"
              style={{
                background: "var(--oh-accent-solid)",
                color: "var(--oh-accent-contrast)",
              }}
            >
              {devisOccupe
                ? "Création…"
                : visite.devis_id
                  ? "Ouvrir le devis de cette visite"
                  : "Créer le devis à partir de la visite"}
            </Button>
            {visite.devis_id && (
              <span style={{ color: "var(--oh-text-secondary)" }} className="text-xs">
                Un devis a déjà été créé pour cette visite — le bouton l'ouvre,
                il n'en crée pas un second.
              </span>
            )}
          </div>

          {/* 🔴 LE REFUS DU SERVEUR, RENDU TEL QUEL — chaque cause garde son
              libellé : « à saisir dans la visite » ⛔ n'est PAS « à renseigner
              dans le profil ». */}
          {devisRefus && (
            <div
              style={{
                background: "var(--oh-stat-red-bg)",
                color: "var(--oh-stat-red-text)",
                borderRadius: 8,
              }}
              className="text-sm mt-2 p-3 whitespace-pre-line"
              data-testid="devis-refus"
            >
              {devisRefus}
            </div>
          )}

          {/* ⚠️ RETENU SANS QUANTITÉ : signalé, ⛔ jamais mis à 1. */}
          {(visite.retenus_sans_quantite || []).length > 0 && (
            <div
              style={{
                background: "var(--oh-stat-amber-bg)",
                color: "var(--oh-stat-amber-text)",
                borderRadius: 8,
              }}
              className="text-xs mt-2 p-2"
              data-testid="saisie-sans-quantite"
            >
              Retenu sans quantité — aucune ligne ne sera créée :
              {" "}{visite.retenus_sans_quantite.join(" · ")}
            </div>
          )}
        </div>
      )}

      {/* ── CLÔTURE ET LIVRABLE ────────────────────────────────────────
          🔴 Pour « recherche de fuite », LE RAPPORT EST CE QUI EST VENDU : le
          devis est l'ENTRÉE, le rapport la SORTIE. Le bloc est donc rendu POUR
          LES DEUX SENS — ⛔ un relevé travaux a aussi son rapport. */}
      <div
        style={{
          background: "var(--oh-bg-elevated)",
          border: "0.5px solid var(--oh-border)",
          borderRadius: 8,
          padding: 12,
        }}
        data-testid="saisie-livrable"
      >
        <div style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-sm mb-2 flex items-center gap-2">
          <FileText size={14} /> Rapport de visite
        </div>
        {visite.statut === "terminee" ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* ⛔ Le PDF est SERVI par le backend : on ouvre l'URL, ⛔ on ne
                reconstruit rien côté navigateur. */}
            <a
              href={`/api/visite/saisies/${visite.id}/rapport`}
              target="_blank"
              rel="noreferrer"
              data-testid="ouvrir-rapport"
              style={{
                background: "var(--oh-accent-solid)",
                color: "var(--oh-accent-contrast)",
                borderRadius: 8,
                height: 36,
              }}
              className="inline-flex items-center px-3 text-sm"
            >
              Ouvrir le rapport de visite
            </a>
            <span style={{ color: "var(--oh-text-secondary)" }} className="text-xs">
              Visite clôturée — le relevé n'est plus modifiable.
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={cloturer}
              disabled={devisOccupe || nbManques > 0 || nbBloquantes > 0}
              data-testid="cloturer-visite"
              style={{
                background: cloturePossible
                  ? "var(--oh-accent-solid)" : "var(--oh-bg-surface)",
                color: cloturePossible
                  ? "var(--oh-accent-contrast)" : "var(--oh-text-muted)",
              }}
            >
              Clôturer la visite
            </Button>
            {/* 🔴 DEUX MOTIFS, JAMAIS FONDUS. Le bouton est désactivé par l'un
                COMME par l'autre, mais la PHRASE dit LEQUEL — un manque appelle
                UNE SAISIE À FAIRE, une réserve bloquante appelle UNE ACTION DE
                CHANTIER. Les confondre enverrait le visiteur chercher un champ à
                remplir là où il faut une reprise. */}
            <span
              style={{
                color: nbBloquantes > 0
                  ? "var(--oh-stat-red-text)" : "var(--oh-text-secondary)",
              }}
              className="text-xs"
              data-testid="saisie-motif-cloture"
            >
              {nbBloquantes > 0
                ? `${nbBloquantes} réserve(s) BLOQUANTE(S) — à traiter ou à lever avant de clôturer.`
                : nbManques > 0
                  ? "Il reste des éléments à renseigner ci-dessous."
                  : "Tout est renseigné : la visite peut être clôturée."}
            </span>
            {/* 🔑 LE RAPPORT RESTE GÉNÉRABLE. Le refuser avant clôture serait
                absurde au moment précis où il sert le plus : MONTRER CE QUI
                BLOQUE. Il sortira marqué « RAPPORT PROVISOIRE ». */}
            <a
              href={`/api/visite/saisies/${visite.id}/rapport`}
              target="_blank"
              rel="noreferrer"
              data-testid="saisie-rapport-provisoire"
              style={{ color: "var(--oh-accent-text)" }}
              className="text-xs underline"
            >
              Voir le rapport provisoire
            </a>
          </div>
        )}
      </div>

      {/* ── LES RÉSERVES BLOQUANTES — un bloc À PART, ⛔ JAMAIS mêlé aux manques.
             ⚠️ Ce ⛔ ne sont PAS des données manquantes : ce sont des CONSTATS À
             TRAITER. Rouge (et non ambre) parce que la sortie n'est pas la même :
             lever la réserve, changer sa gravité, ou corriger l'échelle du profil.
             ⛔ Aucune des trois ⛔ n'efface le constat — il reste au rapport. ── */}
      {nbBloquantes > 0 && (
        <div
          style={{
            background: "var(--oh-stat-red-bg)",
            color: "var(--oh-stat-red-text)",
            borderRadius: 8,
            padding: 12,
          }}
          data-testid="saisie-bloquantes"
        >
          <div className="text-sm font-medium mb-1 flex items-center gap-2">
            <AlertTriangle size={14} /> {nbBloquantes} réserve(s) bloquante(s)
            interdisent la clôture
          </div>
          <ul className="text-xs space-y-0.5">
            {bloquantes.map((r, i) => (
              <li key={r.id || i}>· {r.libelle} — {r.gravite_libelle}</li>
            ))}
          </ul>
          <div className="text-xs mt-1" style={{ opacity: 0.85 }}>
            Le rapport reste consultable : il montre ce qui bloque.
          </div>
        </div>
      )}

      {/* ── CE QUI MANQUE POUR CLÔTURER — venu du SERVEUR ──────────────── */}
      {(visite.manques || []).length > 0 && (
        <div
          style={{
            background: "var(--oh-stat-amber-bg)",
            color: "var(--oh-stat-amber-text)",
            borderRadius: 8,
            padding: 12,
          }}
          data-testid="saisie-manques"
        >
          <div className="text-sm font-medium mb-1 flex items-center gap-2">
            <AlertTriangle size={14} /> Il reste {visite.manques.length} élément(s)
            avant de clôturer
          </div>
          <ul className="text-xs space-y-0.5">
            {visite.manques.map((m, i) => <li key={i}>· {m}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
